import React, { Fragment, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';
import Button from 'reactstrap/lib/Button';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';

import { CmsEntities } from 'client/data/models/cms';
import { HERO_SIZE } from 'site-modules/shared/constants/editorial/editorial';
import { getFullImageUrl, getStaticImageUrl, getValidImageUrl, SOURCES } from 'client/utils/image-helpers';
import { HtmlVideoPlayer } from 'site-modules/shared/components/html-video-player/html-video-player';
import { ClippedContainer } from 'site-modules/shared/components/clipped-container/clipped-container';
import edmundsLogo from 'client/images/logo-white.svg';

import './hero-content-curve.scss';

const TOP_RATED_LOGO = '/images/top-rated/2024/top-rated-page-v1.svg';

const DESKTOP_BACKGROUND_MASK = {
  name: 'background-mask',
  path: 'M0,0.75 L0,0 H1 V0.69 C0.899,0.719,0.827,0.789,0.723,0.87 C0.541,1,0.193,0.908,0,0.75',
};

const DESKTOP_IMAGE_MASK = {
  name: 'hero-mask',
  path: 'M0,0.754 V-0.001 L1,-0.001 V0.685 C0.875,0.701,0.801,0.779,0.686,0.871 C0.513,1,0.183,0.908,0,0.754',
};

const MOBILE_BACKGROUND_MASK = {
  name: 'background-mask',
  path: 'M0.089,0.981 C0.059,0.981,0.03,0.981,0,0.981 V-0.019 H1 V0.873 C0.961,0.884,0.921,0.896,0.879,0.907 C0.667,0.959,0.39,0.981,0.091,0.981 C0.09,0.981,0.089,0.981,0.089,0.981',
};

const MOBILE_IMAGE_MASK = {
  name: 'hero-mask',
  path: 'M0.089,0.981 C0.059,0.981,0.03,0.981,0,0.981 V-0.019 H1 V0.853 C0.961,0.874,0.921,0.886,0.879,0.897 C0.667,0.949,0.39,0.981,0.091,0.981 C0.09,0.981,0.089,0.981,0.089,0.981',
};

export function HeroContentCurve({
  content,
  className = null,
  showOverlayContent = true,
  isMobile = false,
  contentTitle = '',
  showH1Content = true,
  showContentBgSwoop = true,
}) {
  const [videoPlayback, toggleVideoPlayback] = useToggle(true);
  const ref = useRef();

  const videoUrlLg = content.metadata('videoLgURL').value() || content.metadata('videoURL').value();
  const videoUrlSm = content.metadata('videoSmURL').value() || content.metadata('videoURL').value();
  const videoUrl = isMobile ? videoUrlSm : videoUrlLg;
  const photoUrlLg = content.metadata('heroLgURL').value() || get(content.links(), '[0].href');
  const photoUrlSm = content.metadata('heroSmURL').value() || get(content.links(), '[0].href');
  const photoUrl = isMobile ? photoUrlSm : photoUrlLg;

  useEffect(() => {
    if (ref.current) {
      ref.current.classList.add('loaded');
    }
  }, []);

  if (!(videoUrl || photoUrl)) {
    return null;
  }
  const mediaHeight = isMobile ? 420 : 580;

  return (
    <figure className={classnames('hero-content-curve pos-r m-0', className)} ref={ref}>
      <ClippedContainer mask={isMobile ? MOBILE_BACKGROUND_MASK : DESKTOP_BACKGROUND_MASK} containerClasses="hero-bg">
        {showContentBgSwoop && <div className="hero-content-bg w-100 h-100 pos-a left-0 top-0" />}
        <ClippedContainer mask={isMobile ? MOBILE_IMAGE_MASK : DESKTOP_IMAGE_MASK} containerClasses="hero-bg pos-r">
          {videoUrl ? (
            <Fragment>
              <img
                className="w-100 pos-a"
                src={getValidImageUrl(videoUrl, SOURCES.STATIC).replace('mp4', 'jpg')}
                alt=""
                fetchPriority="high"
                width="100%"
                height="100%"
                style={{ objectFit: 'cover' }}
              />
              <HtmlVideoPlayer
                className="hero-video w-100 d-block pos-r"
                height={mediaHeight}
                src={getValidImageUrl(videoUrl, SOURCES.STATIC)}
                autoPlay
                loop
                playsInline
                playback={videoPlayback}
                poster={getValidImageUrl(videoUrl, SOURCES.STATIC).replace('mp4', 'jpg')}
              />
              <div className="video-controls d-flex pos-a">
                <Button
                  className="play-pause-btn d-flex bg-transparent align-items-center justify-content-center p-0_75 p-md-1_25"
                  onClick={toggleVideoPlayback}
                  title={videoPlayback ? 'Pause' : 'Play'}
                >
                  <span
                    className={classnames('size-20 text-white', {
                      'icon-pause21': videoPlayback,
                      'icon-play41': !videoPlayback,
                    })}
                    aria-hidden
                  />
                </Button>
              </div>
            </Fragment>
          ) : (
            <img
              className="hero-image w-100"
              src={getFullImageUrl(photoUrl, HERO_SIZE[isMobile])}
              alt=""
              height={mediaHeight}
              fetchPriority="high"
            />
          )}

          {showH1Content && (
            <h1 className="hero-text pos-a center-x top-0 pt-3_5 mt-0_75 pt-md-2_5 mt-md-0 d-flex flex-column">
              {showOverlayContent && (
                <Fragment>
                  <img src={edmundsLogo} alt="" height={isMobile ? '30' : '40'} />
                  <img
                    src={getStaticImageUrl(TOP_RATED_LOGO)}
                    alt=""
                    height={isMobile ? '75' : '100'}
                    className="mt-0_5"
                  />
                </Fragment>
              )}
              <span
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textIndent: '100%',
                  display: 'inline-block',
                }}
              >
                {' '}
                {contentTitle}{' '}
              </span>
            </h1>
          )}
        </ClippedContainer>
      </ClippedContainer>
    </figure>
  );
}

HeroContentCurve.propTypes = {
  content: CmsEntities.Content.isRequired,
  className: PropTypes.string,
  showOverlayContent: PropTypes.bool,
  isMobile: PropTypes.bool,
  contentTitle: PropTypes.string,
  showH1Content: PropTypes.bool,
  showContentBgSwoop: PropTypes.bool,
};
